import {
  expandAllFeature,
  FeatureImplementation,
  hotkeysCoreFeature,
  propMemoizationFeature,
  selectionFeature,
  syncDataLoaderFeature,
} from "@headless-tree/core";
import { useTree } from "@headless-tree/react";
import classNames from "classnames";
import _ from "lodash";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import expandedIcon from "../../../assets/images/dashboard/sidebar/ExpandedSideBar.svg";
import unexpandedIcon from "../../../assets/images/dashboard/sidebar/NonExpandedSideBar.svg";
import {
  LOI_SHARE_MENU_ID,
  REMINDER_IN_1_WEEK_OR_MORE,
  REMINDER_IN_2_DAYS,
  REMINDER_IN_3_DAYS,
  REMINDER_IN_4_DAYS,
  REMINDER_IN_5_DAYS,
  REMINDER_IN_6_DAYS,
  REMINDER_PAST_DUE,
  REMINDER_TODAY,
  REMINDER_TOMORROW,
  REMINDER_TREE_ROOT_NODES,
  SETTINGS_TYPE,
  SIDE_BAR_TREE,
} from "../../../common/constants";
import {
  DefaultSettingsValue,
  DocumentEntity,
  FieldEntity,
  FilteredDocumentSettingsEntity,
  SideBarEntity,
  SideBarEntityOrdered,
  SideBarType,
  SideBarTypeOrdered,
  TreeDataEntity,
} from "../../../common/types/EntityTypes";
import { ReminderState } from "../../../common/types/SliceTypes";
import {
  calculateSafeModalPosition,
  mergeWithStrictPrecedence,
} from "../../../common/util";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  setPivotSelectedText,
  setSideBarLevelToDocIds,
  setSideBarSelectedParentNode,
  setSideBarSelectedTree,
} from "../../../redux/reducers/dashboardSlice";
import { selectRowsForClients } from "../../../redux/reducers/documentsDataSlice";
import { selectFields } from "../../../redux/reducers/metadataSlice";
import {
  selectDefaultSettingsForSettings,
  selectEnabledSettingsFilterViewMetadata,
  selectUserAutoFilterSettings,
} from "../../../redux/reducers/settingsSlice";
import {
  fetchFFSharedDetailsByUser,
  selectReceivedDocToUserRecord,
  selectRecievedDocIds,
  selectReminders,
  selectSharedDocToUserRecord,
  selectShareDocIds,
  setActiveLOIFFShareLevel,
  setUserSharedLOIList,
} from "../../../redux/reducers/sidebarSlice";
import states from "../../dialog/edit/editable-value/states-constants";
import "./sidebar.scss";
import ApLOISharedBlackIcon from "../../../assets/images/dashboard/header/ap_loi_shared_black_icon.svg";
import {
  selectMenu,
  setIsLOISharePopupActive,
  setLOIPopupOffset,
  setMenu,
} from "../../../redux/reducers/popupSlice";

function extractValue(input: string): string {
  const splitStr = input.split("^^^");
  const updatedStr = splitStr.flatMap((str) => {
    if (str.includes("<Empty>") || str.includes("<Others>")) {
      const regex = /(?:^|%%%)\s*(?!(<Empty>|<Others>))([^%<>]+)\s*(?=%%%|$)/;
      const match = str.match(regex);
      const replaceVal = match ? match[2].trim() : "";
      if (replaceVal !== str)
        return str
          .replaceAll("%", "")
          .replaceAll(replaceVal, "")
          .replaceAll("<Empty>", "<Empty - " + replaceVal + ">")
          .replaceAll("<Others>", "<Others - " + replaceVal + ">");
      else return str;
    } else {
      return str
        .replace(/%%%[^%]+/g, "")
        .replaceAll("%", "")
        .trim();
    }
  });

  return updatedStr.join("^^^") || "";
}

function cleanUserNodeName(name: string) {
  if (name.startsWith("user::")) {
    return name.replace("user::", "");
  }
  return name;
}

function getRawHierarchy(fullId: string): string[] {
  return fullId.split("^^^");
}

function getUIHierarchy(fullId: string): string[] {
  return fullId
    .split("^^^")
    .map((seg) => seg.split("%%%")[0])
    .map((val) => extractValue(val)); // existing normalizer
}

function getHierarchyString(fullId: string): string {
  return getUIHierarchy(fullId).join(" > ");
}

function isTopLevelRoot(item: any) {
  const name = item.getItemName()?.toLowerCase();
  return (
    isRootNode(name) || // inbox, reminder, shared, received
    item.getItemMeta().level === 1 // immediate children under root
  );
}

function isReminderRoot(tree: any) {
  return tree.getRootItem().getItemData().id === "reminder";
}

function getUINameWithCount(
  tree: any,
  item: any,
  uiName: string,
  count: number,
  currentTreeSelection: number
) {
  const marker = "- Site Filter Levels";

  const hideFor = [SIDE_BAR_TREE.REMINDER];

  const countStr =
    hideFor.includes(tree.getRootItem().getItemData().id) &&
    item.getItemMeta().level === 0
      ? ""
      : `(${count})`;
  if (uiName?.includes(marker)) {
    const index = uiName.indexOf(marker);
    return uiName.slice(0, index) + countStr + " " + uiName.slice(index);
  } else {
    return uiName + " " + countStr;
  }
}

function getUIName(itemName: string) {
  if (itemName && Object.keys(REMINDER_TREE_ROOT_NODES).includes(itemName)) {
    itemName =
      REMINDER_TREE_ROOT_NODES[
        itemName as keyof typeof REMINDER_TREE_ROOT_NODES
      ];
  }

  const normalizedItemName = extractValue(itemName);
  const nameSplits = normalizedItemName.split("^^^");
  const nameSplit = nameSplits[nameSplits.length - 1];

  let name = nameSplit;
  const regex = /\(\d+\)/;

  // if (regex.test(nameSplit)) {
  //   const lastParen = nameSplit.lastIndexOf("(");
  //   name = nameSplit.substring(0, lastParen);
  // }

  let addPreSuffix = false;
  if (name === "<Empty>" || name === "<Others>") {
    addPreSuffix = false;
  }

  function capitalizeWordPreservePunct(word: string) {
    return word.replace(/^([^\w]*)(\w)(.*)$/, (_, pre, first, rest) => {
      return pre + first.toUpperCase() + rest;
    });
  }

  const uiName =
    (addPreSuffix ? "<" : "") +
    name
      .split(/\s+/) // split only by spaces
      .map((word) => capitalizeWordPreservePunct(word))
      .join(" ") +
    (addPreSuffix ? ">" : "") +
    (Object.values(SIDE_BAR_TREE).includes(name?.trim()?.toLowerCase()) &&
    SIDE_BAR_TREE.REMINDER !== name.trim().toLowerCase()
      ? " - Site Filter Levels"
      : " ") +
    getDateForOption(name);

  return uiName;
}

function isRootNode(name: string) {
  const transformedName = getUIName(name).split("-")[0]?.trim()?.toLowerCase();
  return Object.values(SIDE_BAR_TREE).includes(transformedName);
}

function getReminderTimeFrame(date: string) {
  const targetDate = moment(date, "YYYY-MM-DDTHH:mm:ss");
  const today = moment().startOf("day");

  const diff = targetDate.diff(today, "days");
  if (diff < 0) {
    return REMINDER_PAST_DUE;
  } else if (diff === 0) {
    return REMINDER_TODAY;
  } else if (diff === 1) {
    return REMINDER_TOMORROW;
  } else if (diff === 2) {
    return REMINDER_IN_2_DAYS;
  } else if (diff === 3) {
    return REMINDER_IN_3_DAYS;
  } else if (diff === 4) {
    return REMINDER_IN_4_DAYS;
  } else if (diff === 5) {
    return REMINDER_IN_5_DAYS;
  } else if (diff == 6) {
    return REMINDER_IN_6_DAYS;
  } else if (diff >= 7) {
    return REMINDER_IN_1_WEEK_OR_MORE;
  }
  return "";
}

function getDateForOption(val: string): string {
  const prefix = " - ";
  const daysMap: Record<string, number> = {
    [REMINDER_TREE_ROOT_NODES.TODAY]: 0,
    [REMINDER_TREE_ROOT_NODES.TOMORROW]: 1,
    [REMINDER_TREE_ROOT_NODES.IN_2_DAYS]: 2,
    [REMINDER_TREE_ROOT_NODES.IN_3_DAYS]: 3,
    [REMINDER_TREE_ROOT_NODES.IN_4_DAYS]: 4,
    [REMINDER_TREE_ROOT_NODES.IN_5_DAYS]: 5,
    [REMINDER_TREE_ROOT_NODES.IN_6_DAYS]: 6,
  };

  if (val === REMINDER_TREE_ROOT_NODES.IN_1_WEEK_OR_MORE) return "";

  const daysToAdd = daysMap[val];
  if (daysToAdd === undefined) return " ";

  const date = moment().add(daysToAdd, "days");
  const label = date.format("ddd");

  moment.updateLocale("en", { week: { dow: 1 } });
  const nextLabel = date.isBetween(
    moment().add(1, "week").startOf("week"),
    moment().add(1, "week").endOf("week"),
    undefined,
    "[]"
  )
    ? `Next Week ${label}`
    : label;

  return prefix + nextLabel;
}

function buildReminderTree(externalId: string, name: string) {
  // Step 1: Generate reminder children dynamically
  const reminderNodes = Object.entries(REMINDER_TREE_ROOT_NODES).reduce(
    (acc, [key, value]) => {
      const id = `${key}`;
      acc[id] = {
        name: value,
        id,
        isFolder: true,
        children: new Set<string>(),
      };
      return acc;
    },
    {} as Record<string, SideBarEntity>
  );

  // Step 2: Parent reminder node with children
  const parentNode: SideBarEntity = {
    name: name,
    id: externalId,
    isFolder: true,
    children: new Set(Object.keys(reminderNodes)),
  };

  // Step 3: Root node
  const rootNode: SideBarEntity = {
    name: "root",
    id: name,
    isFolder: true,
    children: new Set<string>().add(externalId),
  };

  // Step 4: Return merged tree
  return {
    ...reminderNodes,
    [externalId]: parentNode,
    root: rootNode,
  };
}

function createSideBarTreeDS(externalId: string, name: string): SideBarType {
  if (name === SIDE_BAR_TREE.REMINDER) {
    return buildReminderTree(externalId, name);
  } else {
    return {
      [externalId]: {
        name: name,
        id: externalId,
        isFolder: true,
        children: new Set<string>(),
      },
      root: {
        name: "root",
        id: name,
        isFolder: true,
        children: new Set<string>().add(externalId),
      },
    };
  }
}

export function buildSideBarEntities(
  input: Record<string, string[]>
): Map<string, SideBarEntity> {
  const entities = new Map<string, SideBarEntity>();

  for (const fullKey of Object.keys(input)) {
    const parts = fullKey.split("^^^");

    for (let i = 0; i < parts.length; i++) {
      const nodeKey = parts.slice(0, i + 1).join("^^^");

      // create node if missing
      if (!entities.has(nodeKey)) {
        entities.set(nodeKey, { name: nodeKey, id: nodeKey, isFolder: false });
      }

      // if there is a parent, ensure it has children and link this node
      if (i > 0) {
        const parentKey = parts.slice(0, i).join("^^^");
        if (!entities.has(parentKey)) {
          entities.set(parentKey, {
            name: parentKey,
            id: parentKey,
            isFolder: false,
          });
        }
        const parent = entities.get(parentKey)!;
        if (!parent.children) parent.children = new Set<string>();
        parent.children.add(nodeKey);
      }
    }
  }

  // finalize isFolder status
  for (const [key, node] of entities.entries()) {
    if (node.children && node.children.size > 0) {
      node.isFolder = true;
    } else {
      node.isFolder = false;
    }
  }

  return entities;
}

export function flattenToSideBarEntities(
  treeRoots: FilteredDocumentSettingsEntity[]
): SideBarEntity[] {
  const entities: Map<string, SideBarEntity> = new Map();

  function recurse(node: FilteredDocumentSettingsEntity) {
    const hasChildren = !!node.children && node.children.length > 0;

    let childrenSet: Set<string> | undefined;
    if (hasChildren) {
      // Sort children: folders first
      const sortedChildIds = node
        .children!.slice() // copy so original array not mutated
        .sort((a, b) => {
          const aIsFolder = !!a.children && a.children.length > 0;
          const bIsFolder = !!b.children && b.children.length > 0;

          if (aIsFolder === bIsFolder) return 0;
          return aIsFolder ? -1 : 1;
        })
        .map((c) => c.value);

      childrenSet = new Set(sortedChildIds);
    }

    // Add current node
    entities.set(node.value, {
      name: node.value,
      id: node.value,
      isFolder: hasChildren,
      children: childrenSet,
    });

    // Recurse to children
    if (hasChildren) {
      for (const child of node.children!) {
        recurse(child);
      }
    }
  }

  for (const root of treeRoots) {
    recurse(root);
  }

  return Array.from(entities.values());
}

function sanitizeTree(treeData: SideBarType): SideBarType {
  const safe = _.cloneDeep(treeData);

  for (const [id, node] of Object.entries(safe)) {
    if (!node.children) continue;

    // Remove invalid children
    node.children = new Set(
      Array.from(node.children).filter((childId) => !!safe[childId])
    );
  }

  return safe;
}

export function buildTreeEntities(
  input: Record<string, Set<String>>,
  treeType: string | undefined,
  sharedDocsToUser: Record<string, string[]>,
  receivedDocsToUser: Record<string, string[]>
): Record<string, SideBarEntity> {
  const entities: Record<string, SideBarEntity> = {};
  console.debug(
    "[buildTreeEntities] treeType=",
    treeType,
    "inputKeys=",
    Object.keys(input).length,
    "sharedMapKeys=",
    Object.keys(sharedDocsToUser || {}).length,
    "receivedMapKeys=",
    Object.keys(receivedDocsToUser || {}).length
  );

  function ensureNode(id: string, isFolder = false) {
    if (!entities[id]) {
      entities[id] = {
        id,
        name: id,
        isFolder,
        children: new Set(),
      };
    }
  }

  for (const fullKey of Object.keys(input)) {
    if (
      treeType === SIDE_BAR_TREE.SHARED ||
      treeType === SIDE_BAR_TREE.RECEIVED
    ) {
      const mapping =
        treeType === SIDE_BAR_TREE.SHARED
          ? sharedDocsToUser
          : receivedDocsToUser;
      const addedUsers: Record<string, boolean> = {};

      Object.entries(mapping).forEach(([user, docIds]) => {
        if (!addedUsers[user]) {
          ensureNode(`user::${user}`, false);
          entities[`user::${user}`].name = user;
          addedUsers[user] = true;
          [...(entities[`user::${user}`].children || []), ...docIds];
        }
      });
      return entities;
    }

    for (const fullKey of Object.keys(input)) {
      const parts = fullKey.split("^^^");

      for (let i = 0; i < parts.length; i++) {
        const nodeKey = parts.slice(0, i + 1).join("^^^");
        const parentKey = parts.slice(0, i).join("^^^");

        ensureNode(nodeKey);

        if (i > 0) {
          ensureNode(parentKey, true);
          entities[parentKey].children!.add(nodeKey);
        }
      }
    }
  }

  // Final folder check
  for (const id of Object.keys(entities)) {
    entities[id].isFolder = (entities[id].children?.size ?? 0) > 0;
  }

  console.debug(
    "[buildTreeEntities] returning entities keys=",
    Object.keys(entities)
  );

  return entities;
}

function transformDocsToTreeData(
  treesData: SideBarType[],
  inboxDocuments: DocumentEntity[],
  docReminders: ReminderState[],
  sharedDocs: string[],
  receivedDocs: string[],
  mergedAutoSettings: DefaultSettingsValue[],
  sharedDocsToUser: Record<string, string[]>,
  receivedDocsToUser: Record<string, string[]>,
  fields: FieldEntity[]
) {
  let transformedDocsData: Record<string, Record<string, Set<string>>> = {};
  let transformedTreeEntities: Record<
    string,
    Record<string, SideBarEntity>
  > = {};

  const thresholdMap: Record<string, number> = {};
  for (const s of mergedAutoSettings) {
    if (s.name === "minimum_loi")
      thresholdMap[s.subSettingsExternalId] = Number(s.value);
  }

  const REMINDER_PREFIXES = new Set(Object.keys(REMINDER_TREE_ROOT_NODES));

  for (const treeData of treesData) {
    const treeType = treeData["root"].id;
    const subSettingsId = treeData["root"].children?.values().next().value;

    if (subSettingsId) {
      const settingsForId = mergedAutoSettings
        .filter(
          (setting) =>
            setting.subSettingsExternalId === subSettingsId &&
            setting.uiOrder > 0
        )
        .sort((s1, s2) => s1.uiOrder - s2.uiOrder);

      let filterMatchValueToIds: Record<string, Set<string>> = {};

      inboxDocuments &&
        inboxDocuments.forEach((doc) => {
          const docValues = doc.values;
          const reminderDate = doc.reminderDate;
          const parent = doc.parent;

          if (parent !== "") return;

          const isDocumentShared = sharedDocs.find((d) => d === doc.id);
          const isDocumentReceived = receivedDocs.find((d) => d === doc.id);

          if (treeType === SIDE_BAR_TREE.SHARED && !isDocumentShared) return;
          if (treeType === SIDE_BAR_TREE.RECEIVED && !isDocumentReceived)
            return;
          if (treeType == SIDE_BAR_TREE.REMINDER && doc.reminderDate == null)
            return;

          let reminderPrefix = "";
          if (treeType === SIDE_BAR_TREE.REMINDER && reminderDate) {
            reminderPrefix = getReminderTimeFrame(reminderDate);
          }
          let finalFieldValue = reminderPrefix;

          settingsForId.forEach((setting) => {
            const joinedValue = docValues
              .map((val) => {
                if (
                  (setting.name === "field_code" &&
                    setting.value === val.field) ||
                  setting.name === "text"
                ) {
                  const currentFieldName = fields.find(
                    (field) => field.code == val?.field
                  )?.name;

                  let currentFieldValue =
                    val.field === "003m"
                      ? states.find(
                          (state) =>
                            state.name?.toLowerCase() ===
                              val.value?.toLowerCase() ||
                            state.abbr?.toLowerCase() ===
                              val.value?.toLowerCase()
                        )?.name || val.value
                      : val.value;

                  return (
                    (currentFieldValue === "---"
                      ? "<Empty>"
                      : currentFieldValue) +
                    "%%%" +
                    currentFieldName +
                    "%%%"
                  );
                }
                return "";
              })
              .filter((str) => str.length > 0);

            if (joinedValue.length > 0) {
              finalFieldValue =
                finalFieldValue +
                (finalFieldValue.length > 0 ? "^^^" : "") +
                joinedValue;

              if (filterMatchValueToIds[finalFieldValue] === undefined) {
                filterMatchValueToIds[finalFieldValue] = new Set();
              }

              filterMatchValueToIds[finalFieldValue].add(doc.id);
            }
          });
        });

      transformedTreeEntities[treeType] = buildTreeEntities(
        filterMatchValueToIds,
        treeType,
        sharedDocsToUser,
        receivedDocsToUser
      );

      Object.values(transformedTreeEntities[treeType]).forEach((val) => {
        if (!val.id?.includes("^^^")) {
          treeData[subSettingsId].children?.add(val.id);
        } else {
          const valParts = val.id.split("^^^");
          const firstPart = valParts[0];
          if (valParts.length == 2 && REMINDER_PREFIXES.has(firstPart)) {
            treeData[firstPart].children?.add(val.id);
          }
        }
        treeData[val.id] = val;
      });

      const removeLastPart = (s: string, marker = "^^^") =>
        s.includes(marker) ? s.slice(0, s.lastIndexOf(marker)) : s;

      const miniThreshold = thresholdMap[subSettingsId];

      Object.entries(treeData).forEach(([key, val]) => {
        if (
          val.name != "root" &&
          val.isFolder &&
          val.name.includes("<Others")
        ) {
          const targetPath = removeLastPart(val.id);
          treeData[targetPath]?.children?.add(val.id);
        }
      });

      Object.entries(treeData).forEach(([key, val]) => {
        if (
          val.name != "root" &&
          val.isFolder &&
          !REMINDER_PREFIXES.has(val.id) &&
          val.id !== subSettingsId
        ) {
          const srcPath =
            Object.entries(treeData).find(([, entity]) =>
              entity.children?.has(val.id)
            )?.[0] || "";
        }
      });

      transformedDocsData[treeType] = filterMatchValueToIds;
    }
  }

  const daysToCheck: Record<string, number> = {
    TODAY: 0,
    TOMORROW: 1,
    IN_2_DAYS: 2,
    IN_3_DAYS: 3,
    IN_4_DAYS: 4,
    IN_5_DAYS: 5,
    IN_6_DAYS: 6,
  };
  // Logic to disable an Child for the reminder
  for (const tree of treesData) {
    if (tree["root"].id != "reminder") continue;

    for (const prefix of REMINDER_PREFIXES) {
      if (Object.keys(daysToCheck).includes(tree[prefix].id)) {
        const today = moment().startOf("day");

        const futureDate = today
          .clone()
          .add(daysToCheck[tree[prefix].id], "days");

        // 0 = Sunday, 6 = Saturday
        const dayOfWeek = futureDate.day();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

        if (isWeekend) {
          tree["27b70407-ac0a-4050-8983-e54e3e81df11"].children?.delete(
            tree[prefix].id
          );
        }
      }
      tree[prefix].isFolder = false;
    }
  }

  return {
    treeEntityData: treesData,
    treeMatchIdsToDocs: transformedDocsData,
  } as TreeDataEntity;
}

function removeSecondLastAndAppendLast(input: string): string {
  const parts = input.split("^^^");

  if (parts.length <= 2) return input;

  const secondLastIndex = parts.length - 2;

  // Replace second-to-last with "<Others>" + everything from first %%
  parts[secondLastIndex] = parts[secondLastIndex].replace(/^[^%]*/, "<Others>");

  // Remove last element
  parts.pop();

  return parts.join("^^^");
}

function buildNestedLevelCountsWithDocIds(
  data: Record<string, Record<string, Set<string>>>
): {
  counts: Record<string, Record<string, number>>;
  docIds: Record<string, Record<string, string[]>>;
} {
  const counts: Record<string, Record<string, number>> = {};
  const docIds: Record<string, Record<string, string[]>> = {};

  // console.log(data);

  for (const outerKey in data) {
    const inner = data[outerKey];
    const levelCounts: Record<string, number> = {};
    const levelDocIds: Record<string, string[]> = {};

    for (const key in inner) {
      const ids = inner[key]; // Set<string>

      const parts = key.split("^^^");
      let prefix = "";

      for (let i = 0; i < parts.length; i++) {
        prefix = prefix ? prefix + "^^^" + parts[i] : parts[i];

        if (!levelDocIds[prefix]) {
          levelDocIds[prefix] = [];
        }

        for (const id of ids) {
          if (!levelDocIds[prefix].includes(id)) {
            levelDocIds[prefix].push(id);
          }
        }

        levelCounts[prefix] = levelDocIds[prefix].length;
      }
    }

    // Add outer key itself with total count and all docIds
    levelDocIds[outerKey] = [
      ...new Set([...Object.values(inner)].flatMap((s) => [...s])),
    ];
    levelCounts[outerKey] = levelDocIds[outerKey].length;

    counts[outerKey] = levelCounts;
    docIds[outerKey] = levelDocIds;
  }

  // console.log(counts, docIds);

  return { counts, docIds };
}

function sortSidebarChildren(sidebars: SideBarType[]): SideBarType[] {
  return sidebars.map((sidebar) => {
    const sortedSidebar: SideBarType = {};

    for (const [key, entity] of Object.entries(sidebar)) {
      if (!entity.children) {
        sortedSidebar[key] = { ...entity };
        continue;
      }

      const children = Array.from(entity.children)
        .filter((c) => c !== "<Empty>" && c !== "<Others>")
        .sort((a, b) => a.localeCompare(b));

      if (entity.children.has("<Empty>")) {
        children.push("<Empty>");
      }
      if (entity.children.has("<Others>")) {
        children.push("<Others>");
      }

      sortedSidebar[key] = {
        ...entity,
        children: new Set(children),
      };
    }

    return sortedSidebar;
  });
}

const convertTreeMatchIdsToDocs = (
  input: Record<string, Record<string, Set<string>>>
): Record<string, Record<string, string[]>> =>
  Object.fromEntries(
    Object.entries(input).map(([outerKey, innerObj]) => [
      outerKey,
      Object.fromEntries(
        Object.entries(innerObj).map(([innerKey, setVal]) => [
          innerKey,
          Array.from(setVal),
        ])
      ),
    ])
  );

type SideBarProps = {
  containerWidth?: number;
  parentRef?: React.RefObject<HTMLDivElement>;
};

function SideBarV2({ containerWidth, parentRef }: SideBarProps) {
  const dispatch = useAppDispatch();
  const [isMount, setMount] = useState<boolean>(false);

  const [currentSelection, setCurrentSelection] = useState<string>("");
  const [currentTreeSelection, setCurrentTreeSelection] = useState<number>(0);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [focusedItem, setFocusedItem] = useState<string | null>(null);

  let inboxDocuments = useAppSelector(selectRowsForClients);
  const docReminders = useAppSelector(selectReminders);
  const sharedDocs = useAppSelector(selectShareDocIds);
  const receivedDocs = useAppSelector(selectRecievedDocIds);
  const sharedDocsToUser = useAppSelector(selectSharedDocToUserRecord);
  const receivedDocsToUser = useAppSelector(selectReceivedDocToUserRecord);
  const fields = useAppSelector(selectFields);
  const enabledSidebarSettings = useAppSelector(
    selectEnabledSettingsFilterViewMetadata
  );
  const userAutoSettings = useAppSelector(selectUserAutoFilterSettings);

  let defaultAutoSettings =
    useAppSelector((state) =>
      selectDefaultSettingsForSettings(state, SETTINGS_TYPE.AUTO_FILTERED_LOIS)
    ) || [];

  let mergedAutoSettings = mergeWithStrictPrecedence(
    defaultAutoSettings,
    userAutoSettings
  );

  let sortedSideBarData = enabledSidebarSettings
    .filter((setting) => setting.enabled)
    .map((setting) =>
      createSideBarTreeDS(setting.externalId, setting.name?.toLowerCase())
    );

  let data = transformDocsToTreeData(
    sortedSideBarData,
    inboxDocuments,
    docReminders,
    sharedDocs,
    receivedDocs,
    mergedAutoSettings,
    sharedDocsToUser,
    receivedDocsToUser,
    fields
  );

  data["treeEntityData"] = sortSidebarChildren(data.treeEntityData);

  let treeIdToDocCount = buildNestedLevelCountsWithDocIds(
    data.treeMatchIdsToDocs
  );

  data.treeMatchIdsToDocs["reminder"]["27b70407-ac0a-4050-8983-e54e3e81df11"] =
    new Set(treeIdToDocCount?.docIds?.["reminder"]?.["reminder"] ?? []);

  dispatch(
    setSideBarLevelToDocIds(convertTreeMatchIdsToDocs(data.treeMatchIdsToDocs))
  );

  useEffect(() => {
    mergedAutoSettings = mergeWithStrictPrecedence(
      defaultAutoSettings,
      userAutoSettings
    );

    sortedSideBarData = enabledSidebarSettings.map((setting) =>
      createSideBarTreeDS(setting.externalId, setting.name.toLowerCase())
    );

    data = transformDocsToTreeData(
      sortedSideBarData,
      inboxDocuments,
      docReminders,
      sharedDocs,
      receivedDocs,
      mergedAutoSettings,
      sharedDocsToUser,
      receivedDocsToUser,
      fields
    );

    data["treeEntityData"] = sortSidebarChildren(data.treeEntityData);

    treeIdToDocCount = buildNestedLevelCountsWithDocIds(
      data.treeMatchIdsToDocs
    );

    data.treeMatchIdsToDocs["reminder"][
      "27b70407-ac0a-4050-8983-e54e3e81df11"
    ] = new Set(treeIdToDocCount.docIds["reminder"]["reminder"]);

    dispatch(
      setSideBarLevelToDocIds(
        convertTreeMatchIdsToDocs(data.treeMatchIdsToDocs)
      )
    );

    trees.forEach((t) => {
      t.rebuildTree();
    });
  }, [inboxDocuments, userAutoSettings]);

  const customClickBehavior: FeatureImplementation = {
    itemInstance: {
      getProps: ({ tree, item, prev }) => ({
        ...prev?.(),
        onClick: (e: MouseEvent) => {
          if (e.shiftKey) {
            item.selectUpTo(e.ctrlKey || e.metaKey);
          } else if (e.ctrlKey || e.metaKey) {
            item.toggleSelect();
          } else {
            tree.setSelectedItems([item.getItemMeta().itemId]);
          }

          item.setFocused();
        },
      }),
    },
  };

  function mapToOrdered(entity: SideBarEntity): SideBarEntityOrdered {
    let orderedChildren: string[] | undefined;

    const priorityOrder: { [key: string]: number } = {
      PAST_DUE: 0,
      TODAY: 1,
      TOMORROW: 2,
      IN_2_DAYS: 3,
      IN_3_DAYS: 4,
      IN_4_DAYS: 5,
      IN_5_DAYS: 6,
      IN_6_DAYS: 7,
      IN_1_WEEK_OR_MORE: 8,
    };

    const reminderKeys = Object.keys(REMINDER_TREE_ROOT_NODES);

    if (entity.children) {
      // Convert Set -> Array
      const childrenArray = Array.from(entity.children);

      orderedChildren = childrenArray.sort((a, b) => {
        const isOthersA = a.includes("<Others");
        const isOthersB = b.includes("<Others");
        const isEmptyA = a.includes("<Empty");
        const isEmptyB = b.includes("<Empty");

        // Empty always last
        if (isEmptyA && !isEmptyB) return 1;
        if (!isEmptyA && isEmptyB) return -1;

        // Others second last (before Empty)
        if (isOthersA && !isOthersB) return 1;
        if (!isOthersA && isOthersB) return -1;

        // Normal alphabetical for the rest
        if (
          reminderKeys.includes(a.split("^^^")[0]) &&
          reminderKeys.includes(b.split("^^^")[0])
        ) {
          return (
            priorityOrder[a.split("^^^")[0]] - priorityOrder[b.split("^^^")[0]]
          );
        }
        return a.localeCompare(b);
      });
    }

    return {
      name: entity.name,
      id: entity.id,
      isFolder: entity.isFolder,
      children: orderedChildren,
    };
  }

  function mapTreeToOrdered(treeData: SideBarType): SideBarTypeOrdered {
    return Object.fromEntries(
      Object.entries(treeData).map(([key, entity]) => [
        key,
        mapToOrdered(entity),
      ])
    );
  }

  const trees = data.treeEntityData?.map((treeData: SideBarType) => {
    const sanitized = sanitizeTree(treeData);
    const filteredTreeData = mapTreeToOrdered(sanitized);
    return useTree<SideBarEntityOrdered>({
      state: { selectedItems, expandedItems, focusedItem },
      setSelectedItems,
      setExpandedItems,
      setFocusedItem,
      rootItemId: "root",
      getItemName: (item) => item.getItemData().name,
      isItemFolder: (item) => {
        return item.getItemData().isFolder;
      },
      dataLoader: {
        getItem: (itemId) => filteredTreeData[itemId],
        getChildren: (itemId) =>
          Array.from(filteredTreeData[itemId].children || []),
      },
      indent: 5,
      features: [
        syncDataLoaderFeature,
        selectionFeature,
        hotkeysCoreFeature,
        propMemoizationFeature,
        customClickBehavior,
        expandAllFeature,
      ],
    });
  });

  useEffect(() => {
    const currentFocusedItem = trees
      .find(
        (tree) => tree.getRootItem().getItemData().id === SIDE_BAR_TREE.REMINDER
      )
      ?.getState().focusedItem;
    if (currentFocusedItem) {
      dispatch(setSideBarSelectedParentNode(currentFocusedItem));
      dispatch(setPivotSelectedText(currentFocusedItem));
    }
  }, [currentSelection]);

  useEffect(() => {
    dispatch(
      setSideBarSelectedTree(
        trees[currentTreeSelection].getRootItem().getItemData().id
      )
    );
  }, [currentTreeSelection]);

  useEffect(() => {
    setExpandedItems([enabledSidebarSettings[0]?.externalId]);
    setCurrentSelection(REMINDER_TODAY);
    dispatch(setSideBarSelectedParentNode(REMINDER_TODAY));
    dispatch(setPivotSelectedText(REMINDER_TODAY));
    // if (expandedItems.length == 0 && !isMount) {
    //   setExpandedItems([enabledSidebarSettings[0]?.externalId]);
    //   setMount(true)
    // }
  }, []);

  const tree1Ref = useRef<HTMLDivElement | null>(null);
  const tree2Ref = useRef<HTMLDivElement | null>(null);
  const sideBarRef = useRef<HTMLDivElement | null>(null);

  // Apply parent-resize width directly
  useEffect(() => {
    const sidebar = sideBarRef.current;
    if (sidebar) {
      sidebar.style.width = `${containerWidth}px`;
    }
  }, [containerWidth]);

  useEffect(() => {
    const sidebar = sideBarRef.current;
    const tree1 = tree1Ref.current;
    const tree2 = tree2Ref.current;

    if (!sidebar || !tree1 || !tree2) return;

    let frame: number | null = null;

    const syncWidths = () => {
      if (frame) cancelAnimationFrame(frame);

      frame = requestAnimationFrame(() => {
        const sidebarWidth = sidebar.offsetWidth; // Actual visible width

        // Temporarily reset tree2 width to measure natural content width
        const prevWidth = tree2.style.width;
        tree2.style.width = "auto";
        const tree2ContentWidth = tree2.scrollWidth;
        tree2.style.width = prevWidth;

        const finalWidth = Math.max(sidebarWidth, tree2ContentWidth) - 4; // your -4 padding
        const widthPx = `${finalWidth}px`;

        if (tree1.style.width !== widthPx) tree1.style.width = widthPx;
        if (tree2.style.width !== widthPx) tree2.style.width = widthPx;

        sidebar.style.overflowX = finalWidth > sidebarWidth ? "auto" : "hidden";
      });
    };

    syncWidths(); // run initially

    // Only observe tree2 — avoid observing elements we modify!
    const ro = new ResizeObserver(syncWidths);
    ro.observe(tree2);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [containerWidth]); // ← Correct dependency

  function handleApLOISharePopup(
    event: React.MouseEvent<HTMLDivElement>,
    treeId: string
  ) {
    const target = event?.target as HTMLDivElement;

    dispatch(fetchFFSharedDetailsByUser()).then((resp) => {
      if (!resp.payload) {
        console.error("Failed to fetch FF Shared Details");
        return;
      } else {
        const sharedDetails = resp.payload;
        if (sharedDetails?.length === 0) {
          console.warn("No shared FFs found for user");
        } else {
          dispatch(setUserSharedLOIList(sharedDetails));
        }
        const hierarchy = getHierarchyString(treeId);

        if (!target.offsetParent) {
          event?.stopPropagation();
          return;
        }
        // Store tab state
        const [left, top] = calculateSafeModalPosition(
          target,
          Number(sideBarRef.current?.clientWidth) - 8
        );
        dispatch(setLOIPopupOffset([left, top]));
        dispatch(setMenu(LOI_SHARE_MENU_ID));
        dispatch(setIsLOISharePopupActive(true));
        dispatch(setActiveLOIFFShareLevel(hierarchy || ""));
      }
    });

    // Prevent event bubbling
    event?.stopPropagation();
  }

  function getTreeCount(nodeId: string, nodeName: string, treeIndex: number) {
    if (treeIndex < 2) {
      return treeIdToDocCount.counts[nodeId]?.[nodeName] || 0;
    } else if (treeIndex == 2) {
      if (nodeName === SIDE_BAR_TREE.SHARED) {
        return Object.values(sharedDocsToUser).flat().length;
      } else {
        return sharedDocsToUser[nodeName]?.length || 0;
      }
    } else if (treeIndex == 3) {
      if (nodeName === SIDE_BAR_TREE.RECEIVED) {
        return Object.values(receivedDocsToUser).flat().length;
      } else {
        return receivedDocsToUser[nodeName]?.length || 0;
      }
    }
    return 0;
  }

  return (
    <div className="sidebar-background" ref={sideBarRef}>
      {trees?.map((tree, index) => {
        const ref =
          index + 1 === 1 ? tree1Ref : index + 1 === 2 ? tree2Ref : null;

        function isTreeItemSelected(id: string) {
          return id === currentSelection;
        }

        return (
          <div
            key={index}
            {...tree.getContainerProps()}
            className="tree"
            id={`tree${index + 1}`}
            ref={ref as React.RefObject<HTMLDivElement>}
          >
            {tree.getItems().map((item) => (
              <div
                className={classNames(
                  "outeritem",
                  { "tree-root": isRootNode(item.getItemName()) },
                  {
                    "reminder-not-root-level":
                      isReminderRoot(tree) && item.getItemMeta().level != 0,
                  },
                  {
                    "other-root-parent":
                      !isReminderRoot(tree) && item.getItemMeta().level == 1,
                  },
                  {
                    "root-level":
                      item.getItemMeta().level == 0 && isTopLevelRoot(item),
                  },
                  { "tree-background-selected": index == currentTreeSelection },
                  { "item-selected": isTreeItemSelected(item.getId()) }
                )}
                key={item.getId()}
              >
                {/* 🔵 LEFT FIXED ICON ZONE */}
                <div className="left-icon-zone">
                  {index === 1 &&
                    item.getItemMeta().level > 0 &&
                    isTreeItemSelected(item.getId()) && (
                      <div
                        className="left-icon-zone"
                        onClick={(e) => handleApLOISharePopup(e, item.getId())}
                      >
                        <img
                          src={ApLOISharedBlackIcon}
                          className="shared-icon"
                        />
                      </div>
                    )}
                </div>

                {/* 🔵 RIGHT TREE CONTENT WITH LEVEL-BASED PADDING */}
                <div
                  className="right-tree-zone"
                  style={{
                    paddingLeft: `${
                      (item.getItemMeta().level || 0.25) * 1.25
                    }rem`,
                  }}
                >
                  {item.isFolder() && (
                    <button
                      onClick={item.isExpanded() ? item.collapse : item.expand}
                    >
                      <img
                        className={classNames(
                          { "folder-img": item.isFolder },
                          { "leaf-img": !item.isFolder }
                        )}
                        src={item.isExpanded() ? expandedIcon : unexpandedIcon}
                      />
                    </button>
                  )}

                  <button
                    {...item.getProps()}
                    className={classNames({
                      "tree-child-selected":
                        item.isSelected() && isTreeItemSelected(item.getId()),
                    })}
                  >
                    <div
                      className={classNames(
                        "treeitem",
                        {
                          focused: item.isFocused(),
                          expanded: item.isExpanded(),
                          folder: item.isFolder(),
                        },
                        {
                          "tree-root-text": isRootNode(item.getItemName()),
                          "tree-root-child": !isRootNode(item.getItemName()),
                        },
                        {
                          "item-selected": isTreeItemSelected(item.getId()),
                        }
                      )}
                      onClick={() => {
                        setCurrentSelection(item.getId());
                        setCurrentTreeSelection(index);
                      }}
                    >
                      {getUINameWithCount(
                        tree,
                        item,
                        getUIName(cleanUserNodeName(item.getItemName())),
                        // treeIdToDocCount.counts[
                        //   tree.getRootItem().getItemData().id
                        // ]?.[cleanUserNodeName(item.getItemName())] || 0,
                        getTreeCount(
                          tree.getRootItem().getItemData().id,
                          cleanUserNodeName(item.getItemName()),
                          index
                        ),
                        currentTreeSelection
                      )}
                    </div>
                  </button>
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
export default SideBarV2;
